import React, { useEffect } from 'react';
import { MousePointerClick } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ToastAction } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import { useCelebrations } from '@/hooks/useCelebrations';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface CliqueEmailEvent {
  razao_social?: string | null;
  email?: string | null;
  cnpj?: string | null;
  campanha_nome?: string | null;
  consultor_nome?: string | null;
  equipe_nome?: string | null;
}

function formatCnpj(cnpj: string | null | undefined) {
  const digits = String(cnpj || '').replace(/\D/g, '');
  if (digits.length !== 14) return cnpj || 'não informado';
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

export function useNotificacaoCliqueEmail() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { notification } = useCelebrations();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`email-clique-notification-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'email_cliques',
          filter: `consultor_id=eq.${user.id}`,
        },
        (payload) => {
          const clique = payload.new as CliqueEmailEvent;
          // O cadastro do site entra na página Click como campanha, mas não é clique de e-mail.
          if ((payload.new as { origem?: string }).origem === 'site') return;
          const empresa = String(clique.razao_social || clique.email || 'Cliente').replace(/^[\d./-]+\s*/, '');
          const campanha = clique.campanha_nome || 'Campanha de e-mail';
          const consultor = clique.consultor_nome || 'Consultor responsável';
          const equipe = clique.equipe_nome || 'Equipe não informada';

          notification();
          toast({
            title: React.createElement(
              'div',
              { className: 'flex min-w-0 items-center gap-1.5 text-[13px] font-medium leading-tight' },
              React.createElement(MousePointerClick, { className: 'h-4 w-4 shrink-0 text-primary' }),
              React.createElement('span', { className: 'truncate' }, `Cliente clicou — ${empresa}`),
            ),
            description: React.createElement(
              'div',
              { className: 'min-w-0 space-y-0.5 text-xs text-muted-foreground leading-tight' },
              React.createElement('div', { className: 'truncate' }, `CNPJ ${formatCnpj(clique.cnpj)} · ${consultor} · ${equipe}`),
              React.createElement('div', { className: 'truncate' }, `Campanha: ${campanha}`),
            ),
            duration: 15000,
            className: 'w-full max-w-[400px] rounded-xl border border-border border-l-4 border-l-brand-borgonha bg-card py-2.5 pl-3.5 pr-2 shadow-md gap-2 overflow-hidden [&>div]:min-w-0',
            action: React.createElement(
              ToastAction,
              {
                altText: 'Ver cliente na página Click',
                className: 'bg-primary text-primary-foreground hover:bg-primary/90 border-0 rounded-md h-7 px-3 text-xs font-medium shrink-0',
                onClick: () => {
                  try {
                    window.location.assign('/comercial?tab=cliques');
                  } catch {
                    navigate('/comercial?tab=cliques');
                  }
                },
              },
              'Ver',
            ),
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [navigate, notification, toast, user?.id]);
}
